import React, { useState } from "react";
import { toast } from "sonner";
import { Pause } from "@phosphor-icons/react";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PauseReasonDialog } from "@/components/analytics/PauseReasonDialog";

const STATE = { skip_buyer: "Skipping this buyer", stop_all: "All leads stopped", pause_page: "Page paused" };

function choices(kind, r) {
  if (kind === "vendor") {
    return r.can_pause ? [
      { action: "skip_buyer", label: "Skip this buyer", hint: "Their leads go to the next matching buyer.", on: r.skipped },
      { action: "stop_all", label: "Stop all their leads", hint: "Their leads are refused for every buyer until you undo.", on: r.stopped },
    ] : [];
  }
  return [
    r.can_skip && { action: "skip_buyer", label: "Skip this buyer", hint: "This funnel's leads go to the next matching buyer.", on: r.skipped },
    r.page_id && { action: "pause_page", label: "Pause this page for everyone", hint: "Takes the page offline for every buyer and affiliate.", on: r.page_paused },
  ].filter(Boolean);
}

// Pause / undo controls for one affiliate or funnel row of the buyer rejections report.
export function RejectionRowActions({ buyerId, kind, row, onChanged, testId }) {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const opts = choices(kind, row);
  const active = opts.filter((o) => o.on);
  const idle = opts.filter((o) => !o.on);
  const send = async (action, on, note = "", share = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/_admin/buyer-rejections/${on ? "pause" : "unpause"}`, { buyer_id: buyerId, kind, id: row.id, action, note, share_note: share });
      toast.success(on ? `${STATE[action]}${data?.emailed ? " — the affiliate has been emailed" : ""}` : "Undone — traffic is flowing again");
      onChanged();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't update");
    } finally {
      setBusy(false);
    }
  };
  if (!active.length && !(row.flagged && idle.length)) return null;
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {active.map((o) => (
        <span key={o.action} className="inline-flex items-center gap-1 whitespace-nowrap bg-amber-200 px-1 text-[10px] uppercase tracking-widest" data-testid={`${testId}-state-${o.action}`}>
          {STATE[o.action]}
          <button type="button" disabled={busy} onClick={() => send(o.action, false)} className="font-bold underline" data-testid={`${testId}-undo-${o.action}`}>Undo</button>
        </span>
      ))}
      {row.flagged && idle.length > 0 && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button type="button" disabled={busy} data-testid={`${testId}-pause`}
                    className="inline-flex items-center gap-1 whitespace-nowrap border border-black bg-black px-2 py-0.5 text-[10px] uppercase tracking-widest text-white transition-colors duration-100 hover:bg-red-700 disabled:opacity-50">
              <Pause size={10} weight="bold" /> Pause
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 rounded-none border-black">
            {idle.map((o) => (
              <DropdownMenuItem key={o.action} onSelect={() => setPending(o)} data-testid={`${testId}-do-${o.action}`}
                                className="flex cursor-pointer flex-col items-start gap-0 rounded-none">
                <span className="text-xs font-bold">{o.label}</span>
                <span className="text-[11px] text-zinc-600">{o.hint}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <PauseReasonDialog option={pending} kind={kind} name={row.name} onCancel={() => setPending(null)}
                         onConfirm={(note, share) => { const o = pending; setPending(null); send(o.action, true, note, share); }} />
    </div>
  );
}
